/*!
 * strokePath.js — points to an SVG path, and back down to fewer points
 * © 2025 Rob Canning — GPLv3
 *
 * Shared by the two things that make strokes: the session drawing layer,
 * where the points come from a finger, and draw(), where they come from
 * whatever is animating an element. Pure arithmetic on {x, y} — no DOM, no
 * document — so both can be tested without a browser.
 */

/**
 * A smoothed path through the points: each point becomes the control point of
 * a quadratic whose ends are the midpoints of its neighbours, which rounds the
 * corners a sampled line always has without pulling the curve off the points.
 */
export function buildPathD(points) {
    if (!points || points.length === 0) return "";
    if (points.length === 1) {
        const p = points[0];
        return `M ${p.x} ${p.y} L ${p.x + 0.1} ${p.y}`;
    }

    let d = `M ${points[0].x} ${points[0].y}`;

    if (points.length === 2) {
        d += ` L ${points[1].x} ${points[1].y}`;
        return d;
    }

    for (let i = 1; i < points.length - 1; i++) {
        const cp = points[i];
        const next = points[i + 1];
        const mx = (cp.x + next.x) / 2;
        const my = (cp.y + next.y) / 2;
        d += ` Q ${cp.x} ${cp.y} ${mx} ${my}`;
    }

    const last = points[points.length - 1];
    d += ` L ${last.x} ${last.y}`;

    return d;
}

/** Distance between two {x, y}. */
export function distance(a, b) {
    const dx = a.x - b.x, dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}

/** Perpendicular distance from p to the line through a and b. */
function perpendicular(p, a, b) {
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return distance(p, a);
    return Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / len;
}

/**
 * Ramer–Douglas–Peucker: drop every point that lies within `tolerance` of the
 * line its neighbours already describe. A pen sampled once a frame puts down
 * points far closer together than the curve needs, and the cost of carrying
 * them is paid on every repaint.
 *
 * Iterative rather than recursive on purpose — the recursion is O(n) deep in
 * the worst case, which for a long trail is a blown stack rather than a slow
 * frame.
 */
export function simplifyPoints(points, tolerance = 0.6) {
    const n = points?.length ?? 0;
    if (n < 3 || !(tolerance > 0)) return points ? [...(points || [])] : [];

    const keep = new Uint8Array(n);
    keep[0] = keep[n - 1] = 1;
    const stack = [[0, n - 1]];

    while (stack.length) {
        const [first, last] = stack.pop();
        let worst = 0, index = -1;
        for (let i = first + 1; i < last; i++) {
            const d = perpendicular(points[i], points[first], points[last]);
            if (d > worst) { worst = d; index = i; }
        }
        if (index !== -1 && worst > tolerance) {
            keep[index] = 1;
            stack.push([first, index], [index, last]);
        }
    }

    const out = [];
    for (let i = 0; i < n; i++) if (keep[i]) out.push(points[i]);
    return out;
}
